package com.appsyde.shared.presentation.screen.component.ticket

import androidx.compose.runtime.Composable
import com.appsyde.shared.modules.ticket.domain.model.TicketStatus
import com.appsyde.shared.presentation.style.style.AppStylesTickets
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** "Alice Dupont" -> "AD", "Alice" -> "A" */
private fun initialsFromDisplayName(displayName: String): String {
    val parts = displayName.trim().split(" ").filter { it.isNotBlank() }
    return when {
        parts.isEmpty() -> "?"
        parts.size == 1 -> parts[0].first().uppercaseChar().toString()
        else -> "${parts[0].first().uppercaseChar()}${parts[1].first().uppercaseChar()}"
    }
}

@Composable
fun TicketRow(
    row: TicketRowUi,
    isSelected: Boolean = false,
    onClick: () -> Unit = {},
    titleOverride: String? = null
) {
    val displayTitle = titleOverride ?: row.summary

    val statusClass = when (row.status) {
        TicketStatus.OPEN -> AppStylesTickets.statusDotOpen
        TicketStatus.WAITING -> AppStylesTickets.statusDotWaiting
        else -> AppStylesTickets.statusDotClosed
    }

    val baseRowClass = when (row.status) {
        TicketStatus.CLOSED -> AppStylesTickets.rowCardClosed
        else -> AppStylesTickets.rowCard
    }
    Div(attrs = {
        val allClasses = listOfNotNull(baseRowClass) + if (isSelected) listOf(AppStylesTickets.rowCardSelected) else emptyList()
        classes(*allClasses.toTypedArray())
        onClick { onClick() }
    }) {
        Div({ classes(AppStylesTickets.rowMain) }) {

            // ---------- L1: [Dot] Titre + badge manquantes (si besoin) | âge, statut ----------
            Div({ classes(AppStylesTickets.rowTitleLine, AppStylesTickets.rowTitleLineV2) }) {
                Div({ classes(AppStylesTickets.rowTitleLeft) }) {
                    Div({
                        if (isSelected) {
                            classes(AppStylesTickets.statusDotInline, AppStylesTickets.statusDot, statusClass)
                        } else {
                            classes(AppStylesTickets.statusDotInline, AppStylesTickets.statusDot)
                        }
                    })
                    val titleClasses = if (isSelected)
                        listOf(AppStylesTickets.rowTitle, AppStylesTickets.rowTitleSelected)
                    else
                        listOf(AppStylesTickets.rowTitle, AppStylesTickets.rowTitleUnselected)
                    val titleParts = displayTitle.split(" · ").map { it.trim() }.filter { it.isNotBlank() }
                    Div({ classes(*titleClasses.toTypedArray()) }) {
                        if (titleParts.isEmpty()) {
                            Text(displayTitle)
                        } else {
                            titleParts.forEachIndexed { index, part ->
                                if (index > 0) {
                                    Span({ classes(AppStylesTickets.rowTitleSep) }) { Text(" · ") }
                                }
                                val partClass = when (index) {
                                    0 -> AppStylesTickets.rowTitlePart1
                                    1 -> AppStylesTickets.rowTitlePart2
                                    else -> AppStylesTickets.rowTitlePart3
                                }
                                Span({ classes(partClass) }) { Text(part) }
                            }
                        }
                    }
                    // Icône "choses à compléter" (infos manquantes ou à confirmer), sans texte
                    if (row.missingCount > 0 || row.confirmCount > 0) {
                        Span({
                            classes(AppStylesTickets.rowTitleMissingIcon)
                            attr("title", if (row.missingCount > 0) "Infos manquantes" else "À confirmer")
                        }) { Text("📋") }
                    }
                }
                Div({ classes(AppStylesTickets.rowTitleRight) }) {
                    if (row.unread) {
                        Div({ classes(AppStylesTickets.unreadDotInline) })
                    }
                    if (row.showRelativeAge) {
                        Span({ classes(AppStylesTickets.rowAgeInline) }) { Text(row.age) }
                    }
                    val statusColorClass = when (row.businessStatusColorKey) {
                        "traite" -> AppStylesTickets.rowBusinessStatusTraite
                        "a_traiter_neutral" -> AppStylesTickets.rowBusinessStatusATraiterNeutral
                        "a_traiter" -> AppStylesTickets.rowBusinessStatusATraiter
                        "a_verifier" -> AppStylesTickets.rowBusinessStatusAVerifier
                        "en_attente_client" -> AppStylesTickets.rowBusinessStatusEnAttenteClient
                        else -> AppStylesTickets.rowBusinessStatusATraiterNeutral
                    }
                    // Ne pas afficher "À traiter" ni "À vérifier" quand la date est affichée (ex. "il y a 2 h") — redondant
                    val showStatusLabel = row.businessStatusLabel != "À traiter" && row.businessStatusLabel != "À vérifier"
                    if (showStatusLabel) {
                        val usePill = row.businessStatusColorKey == "a_traiter" || row.businessStatusColorKey == "a_verifier"
                        val statusClasses = listOf(
                            AppStylesTickets.rowBusinessStatusLabel,
                            statusColorClass
                        ) + if (usePill) listOf(AppStylesTickets.rowBusinessStatusPill) else emptyList()
                        Span(attrs = { classes(*statusClasses.toTypedArray()) }) { Text(row.businessStatusLabel) }
                    }
                    if (row.assigneeDisplayName != null) {
                        val initials = initialsFromDisplayName(row.assigneeDisplayName)
                        Div(attrs = {
                            classes(AppStylesTickets.rowAssigneeCircle)
                            attr("title", row.assigneeDisplayName)
                        }) {
                            Span({ classes(AppStylesTickets.rowAssigneeCircleText) }) { Text(initials) }
                        }
                    }
                }
            }

            // ---------- L2: pills commentées (simplification row) — Analyse en cours / problème / Manque : X ----------
            // if (row.extractInProgress || row.problemBadge != null || row.missingBadges.isNotEmpty()) {
            //     Div({ classes(AppStylesTickets.rowBadgeLine) }) {
            //         Div({ classes(AppStylesTickets.rowBadgeLineLeft) }) {
            //             if (row.extractInProgress) {
            //                 Div({ classes(AppStylesTickets.rowBadgeAnalyseEnCours) }) {
            //                     Span({ classes(AppStylesTickets.metaIcon) }) { Text("◇") }
            //                     Span({ classes(AppStylesTickets.metaText) }) { Text("Analyse en cours") }
            //                 }
            //             }
            //             if (row.problemBadge != null) {
            //                 Div({ classes(AppStylesTickets.rowBadgeProblem) }) {
            //                     Span({ classes(AppStylesTickets.metaIcon) }) { Text("🧩") }
            //                     Span({ classes(AppStylesTickets.metaText) }) { Text(row.problemBadge) }
            //                 }
            //             }
            //             row.missingBadges.take(1).forEach { label ->
            //                 Div({ classes(AppStylesTickets.rowBadgeMissing) }) {
            //                     Span({ classes(AppStylesTickets.metaIconMissing) }) { Text("⛔") }
            //                     Span({ classes(AppStylesTickets.metaText) }) { Text(label) }
            //                 }
            //             }
            //         }
            //     }
            // }
        }
    }
}
